import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';

import { map } from 'rxjs/operators';

import { OauthService } from './oauth.service';
import {Rule, CDSNode, Package, RuleSet, DefaultPackage} from './capd.model';
import { APIURL } from './url.config';
import {throwError} from "rxjs";

/** Service for creating, modifying and retrieving content packages. */
@Injectable()
export class PackageService {

  constructor(private http: HttpClient,
              private auth: OauthService) { }

  tenant: string = "";

  /**
   * Most basic function; gets all existing content packages,
   * including their entire version history.
   *
   * @async
   * @func getPackages
   * @returns {Promise<Package[]>} a list of content packages.
   */
  getPackages() {
    return this.auth.getToken().then(token => {
      const authorization = 'bearer ' + token;
      const options = { headers: new HttpHeaders({
          Authorization : authorization
        }) };

      return this.http.get<Package[]>(APIURL + 'api/contentpackages/', options)
          .toPromise()
          .catch(this.handleError);
    })
        .catch(this.handleError);
  }

  /**
   * Gets unique names (identifier) of content packages.
   *
   * @async
   * @func getNames
   * @returns {Promise<string[]>} a list of package names as strings.
   */
  getNames() {
    return this.auth.getToken().then(token => {
      const authorization = 'bearer ' + token;
      const options = { headers: new HttpHeaders({
        Authorization : authorization
      }) };

      return this.http.get<string[]>(APIURL + 'api/contentpackages/names', options)
        .toPromise()
        .catch(this.handleError);
    })
    .catch(this.handleError);
  }

  /**
   * Gets the rules present in a given package.
   *
   * @async
   * @func getRuleset
   * @param {string} packId - the identifying string of the package.
   * @returns {Promise<Rule[]>} list of rules.
   */
  getRuleset(packId: string) {
    return this.auth.getToken().then(token => {
      const authorization = 'bearer ' + token;
      const options = { headers: new HttpHeaders({
        Authorization : authorization
      }) };

      return this.http.get<Rule[]>(APIURL + 'api/contentpackages/' + packId +
        '/rules', options)
        .toPromise()
        .catch(this.handleError);
    })
    .catch(this.handleError);
  }

  /**
   * Gets the rules present in a given package, including modifications
   * at an org level.
   *
   * @async
   * @func getOrgRuleset
   * @param {string} packId - the identifying string of a package.
   * @param {string} orgId - the organization ID.
   * @returns {Promise<Rule[]>} a list of rules.
   */
  getOrgRuleset(packId: string, orgId: string) {
    return this.auth.getToken().then(token => {
      const authorization = 'bearer ' + token;
      const options = { headers: new HttpHeaders({
        Authorization : authorization
      }) };

      return this.http.get<Rule[]>(APIURL + 'api/contentpackages/' +
        packId + '/orgs/' + orgId + '/rules', options)
        .toPromise()
        .catch(this.handleError);
    })
    .catch(this.handleError);
  }

  /**
   * Adds a completely new package.
   *
   * @async
   * @func addPackage
   * @param {Package} newPack - a new package object.
   */
  addPackage(newPack: DefaultPackage) {
    return this.auth.getToken().then(token => {
      const body = JSON.stringify(newPack);
      const authorization = 'bearer ' + token;
      const options = { headers: new HttpHeaders({
        'Content-Type': 'application/json',
        Authorization : authorization
      }) };

      return this.http.post(APIURL + 'api/contentpackages/', body, options)
        .pipe(map(res => res))
        .toPromise()
        .catch(this.handleError);
    })
    .catch(this.handleError);
  }

  /**
   * Creates a new version based on an existing given package.
   *
   * @async
   * @func duplicatePackage
   * @param {Package} newPack - mostly a duplicate of the previous package,
   *                            but with a new version number.
   * @param {Package} oldPack - the previous version of the package.
   */
  duplicatePackage(newPack: DefaultPackage, oldPack: DefaultPackage) {
    return this.auth.getToken().then(token => {
      const body = JSON.stringify(newPack);
      const authorization = 'bearer ' + token;
      const options = { headers: new HttpHeaders({
        'Content-Type': 'application/json',
        Authorization : authorization
      }) };

      return this.http.post(APIURL + 'api/contentpackages/' + oldPack._id +
        '/', body, options)
        .toPromise()
        .catch(this.handleError);
    })
    .catch(this.handleError);
  }

  /**
   * Adds an array of rules to a given package.
   *
   * @async
   * @func addRulesToPackage
   * @param {Package} pack - the package you'd like to assign rules to.
   * @param {Rule[]} ruleList - the list of rules you are adding.
   */
  addRulesToPackage(pack, ruleList) {
    return this.auth.getToken().then(token => {
      const packId = pack._id;
      const authorization = 'bearer ' + token;
      const options = { headers: new HttpHeaders({
        'Content-Type': 'application/json',
        Authorization : authorization
      }) };

      const url = APIURL + 'api/contentpackages/' + packId + '/rules';
      const body = JSON.stringify(ruleList);

      return this.http.post(url, body, options)
        .toPromise()
        .catch(this.handleError);
    })
    .catch(this.handleError);
  }

  /**
   * Removes a rule from a package.  You can only remove one rule at a time.
   *
   * @async
   * @func removeRule
   * @param {Package} pack - the package you are removing rules from.
   * @param {Rule} rule - the rule you wish to remove.
   */
  removeRule(pack: DefaultPackage, rule) {
    return this.auth.getToken().then(token => {
      const packId = pack._id;
      const ruleId = rule._id;
      const authorization = 'bearer ' + token;
      const url = (APIURL + 'api/contentpackages/' +
        packId + '/rules/' + ruleId + '/');
      const options = { headers: new HttpHeaders({
        Authorization : authorization
      }) };

      return this.http.delete(url, options)
        .toPromise()
        .catch(this.handleError);
    })
    .catch(this.handleError);
  }

  /**
   * Publishes the package by changing its state to "Published"
   * and giving it a release date. Once published, the front end prevents
   * a user from editing the rules it contains.
   *
   * @async
   * @func publishPackage
   * @param {Package} pack - the package to publish.
   */
  publishPackage(pack: DefaultPackage) {
    return this.auth.getToken().then(token => {
      const packId = pack._id;
      const authorization = 'bearer ' + token;
      const options = { headers: new HttpHeaders({
        'Content-Type': 'application/json',
        Authorization : authorization
      }) };

      const today = new Date();
      const thisMonth = today.getMonth() + 1;

      pack.state = 'Published';
      pack.releaseDate = (today.getFullYear() + '-' +
        thisMonth + '-' + today.getDate());

      const url = APIURL + 'api/contentpackages/' + packId + '/';
      const body = JSON.stringify(pack);

      return this.http.put(url, body, options)
        .toPromise()
        .then(res => res)
        .catch(this.handleError);
    })
    .catch(this.handleError);
  }

  /**
   * Given an old version of a content package, grabs information
   * about the awaiting update for the package.
   *
   * @async
   * @func reviewPackageUpdate
   * @param {Package} oldPack - the previous content package version.
   * @returns {Promise<object>} a list of changed rules, listed under the categories
   *                            "Added," "Changed" and "Removed."
   */
  reviewPackageUpdate(oldPack: Package) {
    return this.auth.getToken().then(token => {
      const newVer = oldPack.id.replace(
        /-([^-]*)$/, '-' + oldPack.upgradeVersion);
      const url = (APIURL + 'api/contentpackages/from/' +
        oldPack.id + '/to/' + newVer + '/');
      const authorization = 'bearer ' + token;
      const options = { headers: new HttpHeaders({
        'Content-Type': 'application/json',
        Authorization : authorization
      }) };

      return this.http.get<RuleSet>(url, options)
          .pipe(map(res=>res))
          .toPromise()
          .catch(this.handleError);
    })
    .catch(this.handleError);
  }

  /**
   * Updates a package to a new version for a given organization.
   *
   * @async
   * @func updatePackage
   * @param {Package} oldPack - the package needing an update.
   * @param {CDSNode} org - the organization to update the package for.
   */
  updatePackage(oldPack: Package, org: CDSNode) {
    return this.auth.getToken().then(token => {
      const body = '';
      const newVer = oldPack.id.replace(/-([^-]*)$/, '-' +
        oldPack.upgradeVersion);
      const url = (APIURL + 'api/orgs/' + org.rootId +
        '/contentpackages/from/' + oldPack.id + '/to/' + newVer + '/');
      const authorization = 'bearer ' + token;
      const options = { headers: new HttpHeaders({
        'Content-Type': 'application/json',
        Authorization : authorization
      }) };

      return this.http.post(url, body, options)
        .toPromise()
        .catch(this.handleError);
    })
    .catch(this.handleError);
  }

  /**
   * Contains the logic for filtering rules based on tenant
   *
   * @func shouldRuleDisplayBasedOnTenant
   */
  shouldRuleDisplayBasedOnTenant(rule: any, tenant: String): boolean {
    let result = false;
    if(!rule.customerTenants) {
      result = true;
    } else {
      let customerTenants: String[] = rule.customerTenants.split(',');
      customerTenants.forEach(customerTenant => {
        if(customerTenant && customerTenant.trim() === tenant) {
          result = true;
        }
      });
    }
    return result;
  }

  /**
   * Gets the packages associated with a given rule
   *
   * @async
   * @func getPackageByRule
   * @param {string} ruleId - the identifying string of a rule
   * @returns {Promise<Package[]>} a list of packages.
   */
  getPackageByRule(ruleId: String) {
    return this.auth.getToken().then(token => {
      const url = (APIURL + 'api/contentpackages/byrule/' + ruleId + '/');
      const authorization = 'bearer ' + token;
      const options = { headers: new HttpHeaders({
          'Content-Type': 'application/json',
          Authorization : authorization
        }) };

      return this.http.get<Package[]>(url, options)
          .toPromise()
          .catch(this.handleError);
    })
        .catch(this.handleError);
  }

  /**
   * Handles errors by throwing them.
   */
  private handleError(error: Response) {
    console.error(error);
    return throwError(error || 'Server error');
  }
}
